import styled from "styled-components";
import { useEffect, useState, useMemo } from "react";
import GC01 from "../siteVectorMap/GC01";
import GC02 from "../siteVectorMap/GC02";
import ColorStyle from "../siteVectorMap/ColorStyle";
import TempFullBase from "../siteVectorMap/TempFullBase";
import LacButtons from "./verdandi/LacButtons";
import { useGetExtendedDirectoryByLocation } from "./hooks/useGetExtendedDirectoryByLocation";
import PosButtons from "./verdandi/PosButtons";
import { useSearchParams } from "react-router-dom";
import MachineDisplay from "./verdandi/MachineDisplay";


const ParentContainer = styled.div`
  zoom: 1;
  @media (max-width: 1440px) {
    zoom: 0.8;
  }
  display: flex;
  flex-direction: row;
  height: ${({ $height }) => $height}px;
  overflow: hidden;
  gap: 1rem;
  box-sizing: border-box;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:first-child {
    flex: 2;
  }

  &:last-child {
    flex: 3;
  }
`;

const TopGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 4fr;
  gap: 1rem;
  padding: 1rem;
  box-sizing: border-box;
  flex: 1;
  overflow: hidden;
`;

const SvgBox = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  padding: 0.15rem; /* ← tiny border/margin */  
  box-shadow: var(--shadow-div-sm);
  border-radius: 0.15rem;
  overflow: hidden; /* ← trims the SVG edges cleanly */
`;

const Svg = styled.svg`
  width: 100%;
  max-width: 1400px; /* optional guard */
  display: block;
  background: transparent;
  opacity: 0.7;
`;

const StyledHeading = styled.h4`
  margin: 0 0 0.5rem 0;
  padding-left: 2em;
  color: var(--color-grey-500);
`;

const TopColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 1rem;
`;

// ratio: SvgContainer 1/5, RowContainer 4/5
const SvgContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--backdrop-color);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-div-md);
  border-radius: 1rem;
  overflow: hidden;
`;

// ratio: SvgContainer 1/5, RowContainer 4/5
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  flex: 1;
  background-color: var(--backdrop-color);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-div-md);
  border-radius: 1rem;
  overflow: hidden;
`;

const RowContainer = styled.div`
  display: flex;
  flex: 4;
  width: 100%;
  background-color: var(--backdrop-color);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-div-md);
  border-radius: 1rem;
  overflow: hidden;
`;

// =============================================================================
// Div Flipp styles
// =============================================================================

const FlipWrapper = styled.div`
  perspective: 1400px;
  width: 100%;
  height: 100%;
  position: relative;
`;

const FlipInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const FlipSide = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  backface-visibility: hidden;
  transform-style: preserve-3d;
`;

// FRONT (LAC list)
const FrontSide = styled(FlipSide)`
  z-index: 1;
  opacity: ${({ $flipped }) => ($flipped ? 0.1 : 1)};
  transition: opacity 0.45s ease;
  pointer-events: ${({ $flipped }) => ($flipped ? "none" : "auto")};  
`;

// BACK is the “page” that swings in

const BackSide = styled(FlipSide)`
  z-index: 2;
  display: flex;
  flex-direction: row;

  /* Page turns in from the left edge */
  transform-origin: left center;
  transform: ${({ $flipped }) =>
    $flipped ? "rotateY(0deg)" : "rotateY(90deg)"};
  transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;

  background: rgba(20, 24, 30, 0.9);
  backdrop-filter: blur(6px);
  box-shadow:
    -8px 0 22px rgba(0, 0, 0, 0.35),
    inset 1px 0 0 rgba(255, 255, 255, 0.04);
  border-radius: 0.75rem;
  overflow: hidden;

  /* Disable clicks while hidden */
  pointer-events: ${({ $flipped }) => ($flipped ? "auto" : "none")};

  /* Slight inner shadow to sell the page look */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.15),
      rgba(0, 0, 0, 0.05) 30%,
      rgba(0, 0, 0, 0) 80%
    );
  }

  /* Optional: slight highlight near outer edge */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: 18px;
    pointer-events: none;
    opacity: ${({ $flipped }) => ($flipped ? 1 : 0)};
    transition: opacity 0.3s ease;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 0.08)
    );
  }
`;

// Main POS content on the back page
const BackContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-right: 0.5rem; /* leave a bit of space before the edge strip */
`;

// Right edge with vertical text (clickable to go back)
const BackEdge = styled.button`
  width: 2.6rem;
  border: none;
  outline: none;
  cursor: pointer;
  margin-left: 0.3rem;
  background: linear-gradient(
    180deg,
    rgba(40, 90, 255, 0.3),
    rgba(40, 90, 255, 0.7)
  );
  color: #fff;
  font-size: 1.45rem;
  font-weight: 600;
  letter-spacing: 0.25rem;

  display: flex;
  align-items: center;
  justify-content: center;

  /* Vertical text */
  writing-mode: vertical-rl;
  text-orientation: mixed;
  /* Make text upright */
  transform: rotate(180deg);

  box-shadow:
    -2px 0 8px rgba(0, 0, 0, 0.4),
    inset 0 0 0 1px rgba(255, 255, 255, 0.15);

  &:hover {
    background: linear-gradient(
      180deg,
      rgba(60, 110, 255, 0.4),
      rgba(60, 110, 255, 0.9)
    );
  }

  &:active {
    transform: rotate(180deg) translateX(1px);
  }
`;

// the SVG should fill the space; let preserveAspectRatio do the centering + crop

function Verdandi() {  
  const [gc, setGc] = useState(null);
  const [lacList, setLacList] = useState([]);
  const [lacClicked, setLacClicked] = useState(null);
  const [posClicked, setPosClicked] = useState(null);
  const [showPos, setShowPos] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
  const urlGc = searchParams.get("gc");
  const urlLac = searchParams.get("lac");
  const urlPos = searchParams.get("pos");

  if (urlGc) setGc(urlGc);
  if (urlLac) {
    setLacClicked(urlLac);
    setShowPos(true); // if lac is set, we’re logically “past” LAC selection
  }
  // pos selection later
}, [searchParams]); // run once

useEffect(() => {
  const next = {};
  next.page = "verdandi";
  if (gc) next.gc = gc;
  if (lacClicked) next.lac = lacClicked;
  if (posClicked) next.pos = posClicked;

  setSearchParams(next, { replace: true });
}, [gc, lacClicked, posClicked, setSearchParams]);


  useEffect(() => {
    // whenever GC changes, clear dependent UI state
    setLacList([]);
    setLacClicked(null);
    setPosClicked(null);
    setShowPos(false);
  }, [gc]);

  useEffect(() => {
  // LAC change wipes POS selection
  setPosClicked(null);
  }, [lacClicked]);

  // constants of the original artboard
  const VB_W = 7207.08;
  const VB_H = 2805.63; //3505.63 Originally

  // total vertical crop as fraction (e.g. 0.20 = 20% total, 10% top + 10% bottom)
  const CROP_Y = 0.2;

  const yOffset = (VB_H * CROP_Y) / 2;
  const croppedHeight = VB_H * (1 - CROP_Y);
  const croppedViewBox = `0 ${yOffset} ${VB_W} ${croppedHeight}`;

  const PADDING_TOP_REM = 15;
  const REM_IN_PX = 7; // adjust if root font-size is different
  const paddingTopPx = PADDING_TOP_REM * REM_IN_PX;
  const [height, setHeight] = useState(window.innerHeight);



  useEffect(() => {
    const updateHeight = () => {
      const header = document.getElementById("app-header");
      const headerHeight = header?.offsetHeight + paddingTopPx ?? 0;
      setHeight(window.innerHeight - headerHeight);
    };

    updateHeight(); // run on mount
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [paddingTopPx]);

  // ---------------------------------------------------------------------------
  // PROCESS API DATA
  // ---------------------------------------------------------------------------
  const baseEnabled = !!gc;
  const lacEnabled = !!gc && !!lacClicked;

  const { data, isLoading, error } = useGetExtendedDirectoryByLocation(
    { project: "8016017", gc: gc, lac: null, maxCounter: 50, pos: null },
    baseEnabled
  );

  const {
    data: lacData,
    isLoading: lacIsLoading,
    error: lacError,
  } = useGetExtendedDirectoryByLocation(
    {
      project: "8016017",
      gc,
      lac: lacClicked ? [lacClicked] : null,
      
      pos: null,
    },
    lacEnabled
  );

  // Filtered data with lac and pos clicked
  const posData = useMemo(() => {
  if (!lacData || !posClicked) return [];
  return lacData.filter(x => x.pos === posClicked);
  }, [lacData, posClicked]);

  const posAssemblies = useMemo(() => {
  return posData.flatMap(loc => loc.assemblies ?? []);
}, [posData]);

const wGroups = useMemo(() => {
  const map = new Map();

  for (const asm of posAssemblies) {
    const w = asm.wNumber ?? "Unknown";
    if (!map.has(w)) map.set(w, []);
    map.get(w).push(asm);
  }

  return Array.from(map.entries())
    .map(([wNumber, assemblies]) => ({ wNumber, assemblies }))
    .sort((a, b) => a.wNumber.localeCompare(b.wNumber));
}, [posAssemblies]);


const handleGcChange = (gc) => {
  setGc(gc);
  setLacClicked(false);
  setPosClicked(false);
  setShowPos(false);
}



  // Unique, sorted POS list based on lacData
  const posList = useMemo(() => {
    if (!lacData || !Array.isArray(lacData)) return [];

    const uniquePos = Array.from(
      new Set(
        lacData.map((x) => x.pos).filter(Boolean) // guard against undefined/null
      )
    );

    // Sort numerically-aware, so "1,2,10" is correct even if they're strings "01", "02"
    uniquePos.sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );

    return uniquePos;
  }, [lacData]);

  useEffect(() => {
    const uniqueLacs = Array.from(
      new Set((data ?? []).map((x) => x.lac))
    ).sort();
    setLacList(uniqueLacs);
  }, [data]);

  const handleLacClick = (lac) => {
    setLacClicked(lac);
    setShowPos(true); // flip to show POS side
  };

    const handlePosClick = (pos) => {
    setPosClicked(pos);    
  };

  

  return (
    <ParentContainer $height={height}>
      <Section>
        <TopGridContainer>
          <TopColumn>
            <SvgContainer>
              <StyledHeading>Verðandi – ᚢᛋᚨᚦ</StyledHeading>
              {/* <HeadingSection text="PRODUCTION" /> */}
              <SvgBox>
                <Svg
                  viewBox={`0 0 ${VB_W} ${VB_H}`} // full artboard
                  preserveAspectRatio="xMidYMax meet" // bottom-align when aspect ratios differ
                  shapeRendering="geometricPrecision"
                  data-status="blue" // default palette
                >
                  <ColorStyle />
                  <TempFullBase />

                  <GC01
                    status="yellow"
                    onClick={(gcId) => {
                      handleGcChange("01")                      
                    }}
                  />
                  <GC02
                    status="red-blink"
                    onClick={(gcId) => {
                      handleGcChange("02") 
                    }}
                  />
                </Svg>
              </SvgBox>
            </SvgContainer>

            <ButtonContainer>
              <FlipWrapper>
                <FlipInner>
                  {/* FRONT: LAC buttons */}
                  <FrontSide $flipped={showPos && posList.length > 0}>
                    <LacButtons lacItems={lacList} locations={data?? []} onSelect={handleLacClick} />
                  </FrontSide>

                  {/* BACK: POS buttons + right edge */}
                  <BackSide $flipped={showPos && posList.length > 0}>
                    <BackContent>
                      <PosButtons
                        posItems={posList}
                        locations={lacData ?? []}
                        onSelect={(pos) => {
                          handlePosClick(pos)
                        }}
                      />
                    </BackContent>

                    <BackEdge
                      type="button"
                      onClick={() => {
                        setShowPos(false);
                        setLacClicked(null);
                      }}
                    >
                      CLICK TO RETURN TO LAC
                    </BackEdge>
                  </BackSide>
                </FlipInner>
              </FlipWrapper>
            </ButtonContainer>
          </TopColumn>

          <RowContainer>
            <MachineDisplay machineList={wGroups} />
          </RowContainer>
        </TopGridContainer>
      </Section>
    </ParentContainer>
  );
}

export default Verdandi;
